import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { GraduationCap, Clock, Search, CheckSquare } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import DarkModeToggle from '@/components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";



export default function CourseDirectoryPage({ user, logout }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [courseProgress, setCourseProgress] = useState({});

  // Check if user has CE Membership (includes ce-membership or curbside-consult plans)
  const hasCEMembership = user && subscriptionStatus?.has_subscription && 
    (subscriptionStatus?.has_ce_membership || 
     subscriptionStatus?.active_plans?.includes('ce-membership') ||
     subscriptionStatus?.active_plans?.includes('curbside-consult'));

  // Theme-aware colors
  const colors = {
    pageBg: isDarkMode ? '#092036' : '#F4F4F4',
    headerBg: isDarkMode ? '#092036' : 'rgba(255,255,255,0.9)',
    headerBorder: isDarkMode ? 'transparent' : '#DADEEO',
    heading: isDarkMode ? '#ffffff' : '#092036',
    text: isDarkMode ? '#e2e8f0' : '#092036',
    subtext: isDarkMode ? '#94a3b8' : '#70767C',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    inputBg: isDarkMode ? '#1e3a5f' : 'white',
    inputText: isDarkMode ? '#ffffff' : '#092036',
    border: isDarkMode ? '#1e3a5f' : '#e5e7eb',
    accent: isDarkMode ? '#60a5fa' : '#102862',
    freeBadgeBg: isDarkMode ? '#065f46' : '#dcfce7',
    freeBadgeText: isDarkMode ? '#a7f3d0' : '#166534',
    memberBadgeBg: isDarkMode ? '#1e3a8a' : '#dbeafe',
    memberBadgeText: isDarkMode ? '#93c5fd' : '#1e40af',
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    fetchCategories();
    fetchCourses();
    if (user) {
      fetchSubscriptionStatus();
      fetchCourseProgress();
    }
  }, [user]);

  useEffect(() => {
    fetchCourses();
  }, [selectedCategory]);

  useEffect(() => {
    // Filter courses based on search term
    if (searchTerm) {
      const filtered = courses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses(courses);
    }
  }, [searchTerm, courses]);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      const response = await fetch(`${API}/subscriptions/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // Fetch progress for all courses user has access to
  const fetchCourseProgress = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      const response = await fetch(`${API}/student/all-progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Convert array to object keyed by course_id for easy lookup
        const progressMap = {};
        data.forEach(p => {
          progressMap[p.course_id] = p;
        });
        setCourseProgress(progressMap);
      }
    } catch (error) {
      console.error('Error fetching course progress:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      // Fetch published courses, optionally filtered by category
      const url = selectedCategory 
        ? `${API}/courses-directory?category=${selectedCategory}`
        : `${API}/courses-directory`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      setCourses(data);
      setFilteredCourses(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = (course) => {
    // If user has access, go directly to the course
    const hasAccess = !course.is_paid || hasCEMembership;
    if (hasAccess && user) {
      navigate(`/course/${course.id}`);
    } else {
      // Otherwise go to preview page
      navigate(`/course-preview/${course.id}`);
    }
  };

  // Get button text based on course type, membership status, and progress
  const getButtonText = (course) => {
    const progress = courseProgress[course.id];
    const hasStarted = progress && progress.completed_lessons && progress.completed_lessons.length > 0;
    
    if (!course.is_paid) {
      return hasStarted ? 'Resume Free Course' : 'Start Free Course';
    }
    if (hasCEMembership) {
      return hasStarted ? 'Resume Course' : 'Start Course';
    }
    return 'Unlock with Membership';
  };

  // Get button style based on course type and access
  const getButtonStyle = (course) => {
    if (!course.is_paid) {
      return { background: '#16a34a' }; // Green for free
    }
    if (hasCEMembership) {
      return { background: '#FF6B35' }; // Orange for members with access
    }
    return { background: colors.accent }; // Navy for locked courses
  };

  return (
    <div className="min-h-screen grid-layout-page" style={{ background: colors.pageBg }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white/90 backdrop-blur-md'} border-b`} style={{ backgroundColor: colors.headerBg, borderColor: colors.headerBorder }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0 flex-1" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <span className="text-base sm:text-2xl font-bold truncate" style={{ color: colors.heading }}>Oral Surgery Success</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <DarkModeToggle />
            {user ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                  style={{ borderColor: colors.accent, color: colors.accent }}
                >
                  <span className="hidden sm:inline">Dashboard</span>
                  <span className="sm:hidden">Home</span>
                </Button>
                <Button
                  onClick={logout}
                  variant="ghost"
                  className="text-xs sm:text-sm px-2 sm:px-4"
                  style={{ color: colors.subtext }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => navigate('/auth')}
                className="text-white border-0 text-xs sm:text-sm px-3 sm:px-4"
                style={{ background: colors.accent }}
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 sm:pt-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: colors.heading }}>
            CE Course Directory
          </h1>
          <p className="text-lg mb-8" style={{ color: colors.subtext }}>
            Browse all available continuing education courses
          </p>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="mb-6 flex flex-wrap justify-center gap-2">
              <Button
                variant={!selectedCategory ? "default" : "outline"}
                onClick={() => setSelectedCategory('')}
                className={!selectedCategory ? "text-white" : ""}
                style={!selectedCategory ? { background: colors.accent } : { borderColor: colors.border, color: colors.text }}
              >
                All Courses
              </Button>
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category.id)}
                  className={selectedCategory === category.id ? "text-white" : ""}
                  style={selectedCategory === category.id ? { background: colors.accent } : { borderColor: colors.border, color: colors.text }}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          )}

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: colors.subtext }} />
            <Input
              type="text"
              placeholder="Search courses by title or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-base rounded-full shadow-lg"
              style={{ backgroundColor: colors.inputBg, color: colors.inputText, borderColor: colors.border }}
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-sm" style={{ color: colors.subtext }}>
            Showing {filteredCourses.length} {filteredCourses.length === 1 ? 'course' : 'courses'}
          </p>
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: colors.subtext }}>Loading courses...</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg" style={{ color: colors.subtext }}>
              {searchTerm ? `No courses found matching "${searchTerm}"` : 'No courses available yet.'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 course-cards-grid">
            {filteredCourses.map((course) => (
              <Card 
                key={course.id} 
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                style={{ borderTop: `4px solid ${colors.accent}`, backgroundColor: colors.cardBg }}
                onClick={() => handleCourseClick(course)}
              >
                {/* Course Thumbnail */}
                <div className="h-48 flex-shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #102862 0%, #1e3a8a 100%)' }}>
                  {course.icon_url ? (
                    <img 
                      src={course.icon_url.startsWith('http') ? course.icon_url : `${API}${course.icon_url}`}
                      alt={course.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <GraduationCap className="w-20 h-20 text-white opacity-80" />
                  )}
                </div>

                {/* Course Info */}
                <div className="p-6 course-card-content">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" style={{ color: '#FF6B35' }} />
                    <span className="text-sm font-semibold" style={{ color: '#FF6B35' }}>
                      {course.ce_hours} CE {course.ce_hours === 1 ? 'Hour' : 'Hours'}
                    </span>
                    {course.is_paid ? (
                      hasCEMembership ? (
                        <span className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ background: '#dcfce7', color: '#166534' }}>
                          ✓ UNLOCKED
                        </span>
                      ) : (
                        <span className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ background: colors.memberBadgeBg, color: colors.memberBadgeText }}>
                          MEMBERS ONLY
                        </span>
                      )
                    ) : (
                      <span className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ background: colors.freeBadgeBg, color: colors.freeBadgeText }}>
                        FREE
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold mb-3" style={{ color: colors.heading }}>
                    {course.title}
                  </h3>

                  <p className="text-sm mb-4 line-clamp-3" style={{ color: colors.subtext }}>
                    {course.description}
                  </p>

                  {/* Learning Outcomes Preview */}
                  {course.learning_outcomes && course.learning_outcomes.length > 0 && (
                    <div className="mb-4">
                      <ul className="space-y-1">
                        {course.learning_outcomes.slice(0, 3).map((outcome, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs" style={{ color: colors.subtext }}>
                            <CheckSquare className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                            <span className="line-clamp-1">{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA Button - Pushed to bottom */}
                  <div className="course-card-cta space-y-2">
                    <Button
                      className="w-full text-white border-0"
                      style={getButtonStyle(course)}
                    >
                      {getButtonText(course)}
                    </Button>
                    
                    {/* Membership indicator for paid courses with access */}
                    {course.is_paid && hasCEMembership && (
                      <p className="text-center text-xs" style={{ color: colors.subtext }}>
                        Included with CE Membership
                      </p>
                    )}
                    
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
